import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { isHttpClientError } from "../../../lib/http/errors";
import { listItineraryMoveTargets, moveItinerary } from "../repo/itineraryRepo";
import type { ItineraryMoveResult } from "../types";

/** 编排行程移动的候选、确认、防重和跨行程响应隔离。 */
export function useItineraryMoveRuntime(options: {
  itineraryId: string;
  onMoved: (result: ItineraryMoveResult) => void | Promise<void>;
}) {
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState<string>();
  const [keyword, setKeyword] = useState("");
  const [selectedDemandId, setSelectedDemandId] = useState("");
  const [movingId, setMovingId] = useState<string>();
  const [error, setError] = useState("");
  const pendingRef = useRef(false);
  const generationRef = useRef(0);
  const currentIdRef = useRef(options.itineraryId);
  currentIdRef.current = options.itineraryId;
  const active = Boolean(options.itineraryId && activeId === options.itineraryId);
  const query = useQuery({
    queryKey: ["itinerary", "move-targets", options.itineraryId],
    queryFn: ({ signal }) => listItineraryMoveTargets(options.itineraryId, signal),
    enabled: active, retry: false, staleTime: 0, refetchOnWindowFocus: false,
  });
  useEffect(() => {
    generationRef.current += 1;
    setActiveId(undefined);
    setKeyword("");
    setSelectedDemandId("");
    setError("");
    return () => { generationRef.current += 1; };
  }, [options.itineraryId]);
  const allItems = query.data?.items ?? [];
  const normalized = keyword.trim().toLocaleLowerCase();
  const items = normalized ? allItems.filter((item) => (
    `${item.versionNumber} ${item.projectCode} ${item.projectTitle}`.toLocaleLowerCase().includes(normalized)
  )) : allItems;
  return {
    active, items, selectedDemandId,
    hasTargets: allItems.length > 0,
    moving: movingId === options.itineraryId,
    loading: active && (query.isPending || query.isFetching),
    errorMessage: error || (query.isError
      ? isHttpClientError(query.error) && query.error.code === "ITINERARY_MOVE_SOURCE_INVALID"
        ? "来源行程已变更、归档或所属定制版本不可用，请重新打开预览核对。"
        : "移动目标版本加载失败，请重试。"
      : ""),
    open() {
      generationRef.current += 1;
      setKeyword(""); setSelectedDemandId(""); setError("");
      setActiveId(options.itineraryId);
    },
    cancel() {
      if (pendingRef.current) return;
      generationRef.current += 1;
      setActiveId(undefined); setSelectedDemandId(""); setError("");
    },
    search(value: string) { setKeyword(value); setSelectedDemandId(""); setError(""); },
    selectDemand(demandId: string) { setSelectedDemandId(demandId); setError(""); },
    retry() { setError(""); void query.refetch(); },
    async confirm(replaceExisting = false): Promise<void> {
      if (pendingRef.current) return;
      const target = allItems.find((item) => item.demandId === selectedDemandId);
      if (!active || query.isFetching || query.isError || !target || !query.data) return;
      if (target.hasItinerary && !replaceExisting) return;
      pendingRef.current = true;
      const id = options.itineraryId;
      const generation = generationRef.current;
      const isCurrent = () => currentIdRef.current === id && generationRef.current === generation;
      setMovingId(id); setError("");
      try {
        const moved = await moveItinerary(id, query.data.sourceDemandId, selectedDemandId, replaceExisting);
        for (const key of [["itinerary", "list"], ["itinerary", "side-preview"],
          ["itinerary", "customization-info"], ["customization"]]) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
        if (isCurrent()) {
          // 刷新页面的失败不应误报成移动失败；调用方负责显示刷新结果。
          await options.onMoved(moved);
          if (isCurrent()) setActiveId(undefined);
        }
      } catch (failure) {
        if (!isCurrent()) return;
        const code = isHttpClientError(failure) ? failure.code : "";
        if (isHttpClientError(failure) && code === "CUSTOMIZATION_ITINERARY_TARGET_INCOMPLETE") {
          setError(failure.message);
          return;
        }
        setError(code === "ITINERARY_MOVE_TARGET_CONFLICT"
          ? "目标版本已被关联，候选已刷新，请重新选择并确认替换。"
          : code === "ITINERARY_MOVE_SOURCE_INVALID"
            ? "来源行程已变更、归档或所属定制版本不可用，请重新打开预览核对。"
            : code === "ITINERARY_MOVE_TARGET_INVALID"
              ? "只能移动到当前定制的其他有效版本，请重新选择。"
              : "未能确认移动结果，请重新打开预览核对当前归属后再操作。");
        setSelectedDemandId("");
        void query.refetch();
      } finally {
        pendingRef.current = false;
        setMovingId(undefined);
      }
    },
  };
}
